import React from 'react';
import PropTypes from 'prop-types';

import Typography from '~/components/core/Atomic/Typography';
import useIsConfigurationFieldSupportedByCategories from '~/components/hooks/useIsConfigurationFieldSupportedByCategories';

import CardDialog from '../../../../CardDialog';
import WithConfirm from '../../../../ConfirmModal';
import { FsIconButton, PERMISSION_ACTIONS, PERMISSION_VERBS, PermissionsButtonWrapper } from '../../../../core';
import { useIncidentConfiguration } from '../../../../hooks/useIncidentConfiguration';
import { InvolvedPersonIcon, InvolvedVehicleIcon, TrashIcon } from '../../../../icons';
import { InvolvedPersonsListDetails } from '../../../../InvolvedPerson';

import { DriverDetails } from './DriverInvolved';
import PassengerInvolvedFragment, {
  cleanConfiguredFieldsFormikValuesBeforeSend,
  getInvolvedPassengerValidationFields,
  getPassengerInitialValues,
} from './PassengerInvolved';
import VehicleInvolvedDetails from './VehicleDetails';

import { useStyles } from '../../../../../assets/styles';

const vehiclePartyFields = {
  involved_vehicle: null,
  passengers: [],
  driver: null,
};

function VehicleParty(props) {
  const { classes } = useStyles();
  const { incidentConfiguration } = useIncidentConfiguration();
  const { isFieldSupportedByCategories } = useIsConfigurationFieldSupportedByCategories();

  const {
    disabled,
    title,
    isInsured,
    vehicleParty,
    showOnly,
    onFetchInvolvedPerson,
    onFetchInvolvedProperty,
    onUpdateVehicle,
    onDeleteVehicle,
    shouldHidePassengers,
    shouldCheckDeletePermission = true,
    errors,
  } = props;

  const deleteAction = onDeleteVehicle && (
    <WithConfirm title="Delete Vehicle?" primaryButtonName="Delete" shouldCloseOnPrimary>
      <FsIconButton onClick={onDeleteVehicle} disabled={disabled} icon={TrashIcon} />
    </WithConfirm>
  );
  const action =
    deleteAction &&
    (shouldCheckDeletePermission ? (
      <PermissionsButtonWrapper verb={PERMISSION_VERBS.WRITE} action={PERMISSION_ACTIONS.DELETE_INVOLVED_PARTY}>
        {deleteAction}
      </PermissionsButtonWrapper>
    ) : (
      deleteAction
    ));

  return (
    <CardDialog
      title={
        <>
          {title} {action}
        </>
      }
      outlinedCard
    >
      <div>
        <div className={classes.containerCentered}>
          <InvolvedVehicleIcon />
          &nbsp;
          <Typography style={{ paddingRight: 14 }} display="block" variant="subtitle1">
            Vehicle
          </Typography>
        </div>
        <VehicleInvolvedDetails
          classes={classes}
          disabled={disabled}
          showOnly={showOnly}
          isInsured={isInsured}
          vehicleInvolvedHeader={vehicleParty.involved_vehicle}
          onFetchVehicleDetails={async () => await onFetchInvolvedProperty(vehicleParty.involved_vehicle)}
          onSetVehicleDetails={async (vehicleInvolved) =>
            await onUpdateVehicle('SET_INVOLVED_VEHICLE', vehicleInvolved)
          }
          onSaveVehicleDetails={async (vehicleInvolved) =>
            await onUpdateVehicle('UPDATE_INVOLVED_VEHICLE', vehicleInvolved)
          }
          onDeleteVehicle={
            // No DELETE endpoint for first-party `involved_vehicle` (the BE only exposes
            // it for third-party). Hide the trash on the first-party dialog to avoid 405s.
            !isInsured ? async () => await onUpdateVehicle('DELETE_INVOLVED_VEHICLE', '') : undefined
          }
          error={errors && errors['involved_vehicle']}
        />
      </div>
      {!shouldHidePassengers && (
        <>
          <div className={`${classes.containerCentered} ${classes.containerTitle} ${classes.inputContainer}`}>
            <InvolvedPersonIcon />
            &nbsp;
            <Typography display="block" variant="subtitle1">{`Passengers (including driver): ${
              (vehicleParty.passengers ? vehicleParty.passengers.length : 0) + (vehicleParty.driver ? 1 : 0)
            }`}</Typography>
          </div>
          <DriverDetails
            classes={classes}
            disabled={disabled}
            isInsured={isInsured}
            driver={vehicleParty.driver}
            onFetchDriverDetails={async () => await onFetchInvolvedPerson(vehicleParty.driver)}
            onSetDriverDetails={async (driver) => await onUpdateVehicle('SET_DRIVER', driver)}
            onSaveDriverDetails={async (driver) => await onUpdateVehicle('UPDATE_DRIVER', driver)}
            onDeleteDriver={() => onUpdateVehicle('DELETE_DRIVER', '')}
            shouldCheckDeletePermission={shouldCheckDeletePermission}
            error={errors && errors['driver']}
          />
          <InvolvedPersonsListDetails
            involvedPersons={vehicleParty.passengers}
            shouldCheckDeletePermission={shouldCheckDeletePermission}
            onFetchInvolvedPersonDetails={async (passenger) => await onFetchInvolvedPerson(passenger)}
            onSaveInvolvedPersonDetails={async (passenger, idx) => {
              const configuredFields =
                incidentConfiguration?.involved_parties?.involved_passenger?.configured_fields || [];
              const cleanConfiguredFields = cleanConfiguredFieldsFormikValuesBeforeSend(
                configuredFields,
                passenger.configured_fields_values || {}
              );
              await onUpdateVehicle('UPDATE_PASSENGER', {
                passenger: { ...passenger, configured_fields_values: cleanConfiguredFields },
                idx,
              });
            }}
            onAddInvolvedPerson={async (passenger) => {
              const configuredFields =
                incidentConfiguration?.involved_parties?.involved_passenger?.configured_fields || [];
              const cleanConfiguredFields = cleanConfiguredFieldsFormikValuesBeforeSend(
                configuredFields,
                passenger.configured_fields_values || {}
              );
              await onUpdateVehicle('ADD_PASSENGER', {
                ...passenger,
                configured_fields_values: cleanConfiguredFields,
              });
            }}
            onDeleteInvolvedPerson={async (passenger, idx) =>
              await onUpdateVehicle('DELETE_PASSENGER', { passenger, idx })
            }
            personLabel="Passenger"
            allowDelete
            CustomInvolvedPersonFragment={PassengerInvolvedFragment}
            customInitialFields={getPassengerInitialValues(incidentConfiguration)}
            customValidationFields={getInvolvedPassengerValidationFields(
              incidentConfiguration,
              isFieldSupportedByCategories
            )}
            disabled={disabled}
            contactAcceptedRoles={['insured', 'named_driver', 'spouse', 'family_member', 'claimant', 'other']}
          />
        </>
      )}
    </CardDialog>
  );
}

VehicleParty.propTypes = {
  classes: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  title: PropTypes.string.isRequired,
  vehicleParty: PropTypes.object.isRequired,
  onFetchInvolvedPerson: PropTypes.func.isRequired,
  onFetchInvolvedProperty: PropTypes.func.isRequired,
  onUpdateVehicle: PropTypes.func.isRequired,
  onDeleteVehicle: PropTypes.func,
  isInsured: PropTypes.bool,
  showOnly: PropTypes.bool,
  shouldHidePassengers: PropTypes.bool,
  shouldCheckDeletePermission: PropTypes.bool,
  errors: PropTypes.object,
};

export { vehiclePartyFields };
export default VehicleParty;
